import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { createCheckoutSession, PurchaseType } from '../../services/paymentService';
import DynamicBackground from '../../components/DynamicBackground';
import '../../styles/darkMode.css';
import './BuyCredits.css';

const BuyCredits: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logo = process.env.PUBLIC_URL + '/SOUND_LEGAL_WHITE.png';

  const handlePurchase = async (purchaseType: PurchaseType) => {
    if (!user) {
      setError('Please sign in to purchase credits');
      navigate('/login');
      return;
    }

    setLoading(purchaseType);
    setError(null);

    try {
      const session = await createCheckoutSession({
        userId: user.id,
        purchaseType,
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/buy-credits?cancelled=true`,
      });

      // Redirect to Stripe Checkout
      window.location.href = session.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start payment process. Please try again.');
      setLoading(null);
    }
  };

  return (
    <DynamicBackground>
      <div className="buy-credits-content">
        <img src={logo} alt="SoundLegal.ai Logo" className="credits-logo" />
        <h1 className="credits-title">Buy Credits</h1>
        
        {error && (
          <div className="error-message" style={{ 
            backgroundColor: '#ff4444', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            marginBottom: '1rem' 
          }}>
            {error}
          </div>
        )}

        {user?.subscriptionStatus === 'active' ? (
          <div className="credits-info-card">
            <div className="beta-notice" style={{ backgroundColor: '#4CAF50' }}>
              <h2 className="beta-title">✅ Enterprise Subscription Active</h2>
              <p className="beta-description">
                You have unlimited access to all SoundLegal.ai features!
              </p>
              {user.subscriptionEndDate && (
                <p className="beta-description">
                  Next billing date: {new Date(user.subscriptionEndDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="credits-info-card">
            <div className="beta-notice">
              <h2 className="beta-title">💳 Purchase Credits or Subscribe</h2>
              <p className="beta-description">
                Choose a plan that works best for you. Buy credits for one-time use or subscribe for unlimited access.
              </p>
            </div>

          <div className="future-credits-info">
            <h3 className="future-title">Select Your Plan</h3>
            <p className="future-description">
              Purchase credits for one-time use or subscribe for unlimited monthly access.
            </p>

            <div className="pricing-preview">
              <h4 className="pricing-title">Available Plans</h4>
              <div className="pricing-tiers">
                <div className="pricing-tier">
                  <h5>Starter</h5>
                  <p className="price">$5</p>
                  <p className="credits">1 Credit</p>
                  <p className="description">Perfect for one contract!</p>
                  <button 
                    className="purchase-button"
                    onClick={() => handlePurchase('starter')}
                    disabled={loading !== null}
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {loading === 'starter' ? 'Processing...' : 'Buy Now'}
                  </button>
                </div>
                
                <div className="pricing-tier featured">
                  <h5>Professional</h5>
                  <p className="price">$49.50</p>
                  <p className="credits">12 Credits</p>
                  <p className="description">If you need to analyze or generate multiple contracts!</p>
                  <button 
                    className="purchase-button"
                    onClick={() => handlePurchase('professional')}
                    disabled={loading !== null}
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {loading === 'professional' ? 'Processing...' : 'Buy Now'}
                  </button>
                </div>
                
                <div className="pricing-tier">
                  <h5>Enterprise</h5>
                  <p className="price">$200/Mo</p>
                  <p className="credits">Unlimited Usage Per Month</p>
                  <p className="description">For unlimited usage (good for large companies)</p>
                  <button 
                    className="purchase-button"
                    onClick={() => handlePurchase('enterprise')}
                    disabled={loading !== null}
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#FF9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {loading === 'enterprise' ? 'Processing...' : 'Subscribe'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="action-buttons">
          <button 
            className="action-button primary"
            onClick={() => navigate('/tools')}
          >
            Continue Using Tools
          </button>
          <button 
            className="action-button secondary"
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      </div>
    </DynamicBackground>
  );
};

export default BuyCredits; 